import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { customToast } from "../../../../components/notify";
import {
        IApiTimeAlertGroup,
        IApiTimeAlerts,
        ITimeAlertFormData,
        ITimeAlertGroup,
        ITimeAlertGroupForm,
    } from "../../types";
import { DEFAULT_TIME_GROUP, INITIAL_TIME_ALERTS_SETTINGS } from "../configs";
import { timeAlertsValidationSchema } from "../validations";
import useKioskSettings from "./useKioskSettings";
import { useTabGuard } from "./useTabGuard";
import useUploadAssets from "./useUploadAssets";

interface UseTimeAlertSettingsProps {
    characterId: string;
}

export const useTimeAlertSettings = ({ characterId }: UseTimeAlertSettingsProps) => {
    const { t } = useTranslation();
    const { kioskSetting, isUpdatingKioskSetting, updateTimeAlerts } = useKioskSettings({ characterId });
    const { handleUploadFile, isUploading } = useUploadAssets();

    // ======================= Form =======================
    const convertFormDataToApiPayload = (formData: ITimeAlertFormData): IApiTimeAlerts => ({
        enabled_time_alerts: formData.enabled_time_alerts,
        data: formData.data.map((g): IApiTimeAlertGroup => ({
            ...g,
            media: g.media
                ? { url: g.media.url, asset_id: g.media.asset_id, type: g.media.type }
                : undefined,
        })),
    });

    const convertApiDataToFormData = (apiData: IApiTimeAlerts): ITimeAlertFormData => ({
        enabled_time_alerts: apiData.enabled_time_alerts,
        data: apiData.data.map((g): ITimeAlertGroupForm => ({ ...g })),
    });

    const methods = useForm<ITimeAlertFormData>({
        defaultValues: INITIAL_TIME_ALERTS_SETTINGS,
        resolver: yupResolver(timeAlertsValidationSchema()),
        mode: "onChange",
    });

    const { formState, watch } = methods;

    const {
        fields: dataFields,
        append: appendGroup,
        remove: removeGroup,
    } = useFieldArray({
        control: methods.control,
        name: "data",
    });

    const isDirty = formState.isDirty;
    const isValid = formState.isValid;
    const enabledWatch = useWatch({ control: methods.control, name: "enabled_time_alerts" });
    const watchedFormValues = watch();

    /**
     * Check that every group of type "media" has an uploaded file.
     * Each group must have media.url or media.file (selected file).
     */
    const hasValidMediaFiles = useMemo(() => {
        const data = (watchedFormValues?.data as ITimeAlertGroupForm[] | undefined) ?? [];
        if (!Array.isArray(data)) return true;
        return data.every((group) => {
            if (group?.type !== "media") return true;
            const hasUrl = !!(group.media?.url && String(group.media.url).trim() !== "");
            const hasFile = group.media?.file instanceof File;
            return hasUrl || hasFile;
        });
    }, [watchedFormValues]);

    // ======================= Toggle Handler =======================
    const handleToggle = useCallback(
        (enabled: boolean) => {
            methods.setValue("enabled_time_alerts", enabled, { shouldDirty: true });
        },
        [methods]
    );

    /** 檢查當前組別是否與其他組在同一天有相同時段，若有則回傳錯誤訊息 */
    const validateTimeOverlap = useCallback(
        (data: ITimeAlertGroup[], currentIndex: number): string | null => {
            const current = data[currentIndex];
            if (!current?.time || !current.repeat_days?.length) return null;

            const currentDays = new Set(current.repeat_days);

            // 判斷時段是否衝突
            const conflict = data.findIndex(
                (other, j) => 
                    j !== currentIndex &&
                    other.time === current.time &&
                    other.repeat_days?.some((d) => currentDays.has(d))
            )

            return conflict !== -1
                ? t("APPWIDGET.TIME_ALERTS_OVERLAP", { n: conflict + 1 }) || `與提醒 ${conflict + 1} 在同一天相同時段重複`
                : null;
        },
        [t]
    );


    const onSubmit = useCallback(
        async (formData: ITimeAlertFormData) => {
            // Check for time overlaps before processing
            const hasOverlap = formData.data.some((_, i) => validateTimeOverlap(formData.data, i));
            if (hasOverlap) {
                customToast.error(t("APPWIDGET.TIME_ALERTS_OVERLAP_CANNOT_SAVE") || "存在時段重疊，無法儲存");
                return;
            }

            try {
                const data: ITimeAlertGroup[] = [];

                const processMediaGroup = async (
                    group: ITimeAlertGroupForm,
                    mediaFile: unknown
                ): Promise<ITimeAlertGroup | null> => {
                    const { voice: _v, ...rest } = group;

                    if (mediaFile instanceof File) {
                        const prefix = mediaFile.type.startsWith("video/") ? "video" : "image";
                        const result = await handleUploadFile({ file: mediaFile, prefix });
                        if (!result) {
                            customToast.error(t("COMMON.UPLOAD_FAILED") || "上傳失敗");
                            return null;
                        }
                        return {
                            ...rest,
                            media: {
                                url: result.url,
                                asset_id: result.asset_id,
                                type: prefix === "video" ? "video" : "image",
                            },
                        } as ITimeAlertGroup;
                    }

                    if (group.media?.url) {
                        return rest as ITimeAlertGroup;
                    }

                    customToast.error(t("APPWIDGET.TIME_ALERTS_MEDIA_REQUIRED") || "請為媒體播放上傳檔案");
                    return null;
                };

                for (const [, group] of formData.data.entries()) {
                    if (group.type === "media") {
                        const result = await processMediaGroup(group, group.media?.file);
                        if (!result) {
                            throw new Error("Media processing failed");
                        }
                        data.push(result);
                    } else {
                        const { media: _m, ...rest } = group;
                        data.push(rest as ITimeAlertGroup);
                    }
                }
                const sanitizedFormData: ITimeAlertFormData = {
                    enabled_time_alerts: formData.enabled_time_alerts,
                    data,
                };
                const payload: IApiTimeAlerts = convertFormDataToApiPayload(sanitizedFormData);
                await updateTimeAlerts(payload);
                methods.reset(sanitizedFormData);
                customToast.success(t("COMMON.SETTINGS_SAVED") || "設定已儲存");
            } catch (error) {
                customToast.error(t("COMMON.SETTINGS_ERROR") || "儲存設定失敗");
                throw error;
            }
        },
        [handleUploadFile, updateTimeAlerts, t, validateTimeOverlap, methods]
    );

    const saveHandler = useCallback(async () => {
        await methods.handleSubmit(onSubmit)();
    }, [methods, onSubmit]);

    useTabGuard("time_alerts", isDirty, saveHandler);

    const handleCancel = () => {
        methods.reset();
    };

    const handleAddTimeAlertGroup = useCallback(() => {
        appendGroup({ ...DEFAULT_TIME_GROUP, id: uuidv4() });
    }, [appendGroup]);

    const handleRemoveTimeAlertGroup = useCallback(
        (index: number) => {
            removeGroup(index);
        },
        [removeGroup]
    );

    const handleResetToDefaults = () => {
        methods.reset(INITIAL_TIME_ALERTS_SETTINGS);
    };

    useEffect(() => {
        const timeAlerts = kioskSetting?.data?.native?.time_alerts;
        if (timeAlerts) {
            const formData = convertApiDataToFormData(timeAlerts);
            methods.reset({
                enabled_time_alerts: formData.enabled_time_alerts,
                data: formData.data,
            });
        }
    }, [kioskSetting?.data?.native?.time_alerts, methods]);

    return {
        methods,
        handleSubmit: onSubmit,
        handleCancel,
        handleResetToDefaults,
        isUpdatingKioskSetting,
        isUploading,
        isDirty,
        isValid,
        hasValidMediaFiles,
        dataFields,
        handleAddTimeAlertGroup,
        handleRemoveTimeAlertGroup,
        validateTimeOverlap,
        isEnabled: enabledWatch,
        handleToggle,
    };
};
